/**
 * TwoFactorManageDialog — 自助 2FA 因子管理弹窗（ADM-A9）
 *
 * mode='backup'：current_password 再认证 → POST /me/2fa/backup-codes/regenerate → 明文 10 码一次性展示 + 下载。
 * mode='totp'：current_password → start（新 secret challenge）→ 出示一次 6 位 code → confirm 轮转生效。
 * 强化鉴权材料（非降级）；2FA 强制无「解绑」。明文码 / secret 仅前端一次性展示，不持久化。
 */

import type { TotpBindChallenge } from '@novel-admin/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { meApi } from '~/hooks/use-me';
import { Icons } from '~/lib/icons';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : '操作失败';
}

/** 备用码下载为 txt（明文仅本次展示，提示用户离线保存）。 */
function downloadCodes(codes: string[]) {
  const blob = new Blob([`${codes.join('\n')}\n`], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'novel-admin-backup-codes.txt';
  a.click();
  URL.revokeObjectURL(url);
}

export function TwoFactorManageDialog({
  mode,
  onClose,
}: { mode: 'backup' | 'totp'; onClose: () => void }) {
  const [pwd, setPwd] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [challenge, setChallenge] = useState<TotpBindChallenge | null>(null);

  const regen = useMutation({
    mutationFn: () => meApi.regenerateBackupCodes(pwd),
    onSuccess: (r) => setCodes(r.backup_codes),
    onError: (err) => toast(errMsg(err), { tone: 'neg', icon: 'IconAlert' }),
  });
  const start = useMutation({
    mutationFn: () => meApi.startTotpRebind(pwd),
    onSuccess: (c) => setChallenge(c),
    onError: (err) => toast(errMsg(err), { tone: 'neg', icon: 'IconAlert' }),
  });
  const confirm = useMutation({
    mutationFn: () => meApi.confirmTotpRebind(code),
    onSuccess: () => {
      toast('TOTP 已重新绑定', { tone: 'pos', icon: 'IconShield' });
      onClose();
    },
    onError: (err) => toast(errMsg(err), { tone: 'neg', icon: 'IconAlert' }),
  });

  const title = mode === 'backup' ? '重新生成备用码' : '重新绑定 TOTP 验证器';
  const busy = regen.isPending || start.isPending || confirm.isPending;

  // ── 备用码：已生成 → 展示 + 下载 ──
  if (mode === 'backup' && codes) {
    return (
      <Dialog
        title="新备用码（仅本次展示）"
        subtitle="旧备用码已全部失效 · 请立即离线保存，关闭后无法再次查看"
        onClose={onClose}
        footer={
          <div className="hstack" style={{ gap: 8 }}>
            <button type="button" className="btn btn-sm" onClick={() => downloadCodes(codes)}>
              <Icons.IconDownload size={11} /> 下载
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={onClose}>
              我已保存
            </button>
          </div>
        }
      >
        <DialogBody>
          <div
            className="mono"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 6,
              padding: 12,
              fontSize: 13,
            }}
          >
            {codes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  // ── TOTP：已拿到 challenge → 出示 code 确认 ──
  if (mode === 'totp' && challenge) {
    return (
      <Dialog
        title="确认新 TOTP 验证器"
        subtitle="在验证器 App 中添加下方密钥，再输入一次 6 位验证码确认"
        onClose={onClose}
        footer={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={busy || !/^\d{6}$/.test(code)}
            onClick={() => confirm.mutate()}
          >
            {confirm.isPending ? '确认中…' : '确认轮转'}
          </button>
        }
      >
        <DialogBody>
          <div className="vstack" style={{ gap: 10, padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>密钥（手动添加）：</div>
            <code className="mono" style={{ wordBreak: 'break-all', fontSize: 12 }}>
              {challenge.secret_base32}
            </code>
            <input
              className="input mono"
              placeholder="6 位验证码"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  // ── 初始：current_password 再认证 ──
  const onContinue = () => (mode === 'backup' ? regen.mutate() : start.mutate());
  return (
    <Dialog
      title={title}
      subtitle="敏感操作 · 请先输入当前登录密码"
      onClose={onClose}
      footer={
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={busy || pwd.length === 0}
          onClick={onContinue}
        >
          {busy ? '处理中…' : '继续'}
        </button>
      }
    >
      <DialogBody>
        <div style={{ padding: 12 }}>
          <PasswordInput
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="当前登录密码"
          />
        </div>
      </DialogBody>
    </Dialog>
  );
}
